<template>
  <div
    v-if="renderComponent"
    :brezel-component-id="component.id"
    :style="{ display, flexWrap, justifyContent, overflowX, padding: containerPadding }"
    :class="'button-component ' + className"
  >
    <template
      v-for="(button, index) in buttons"
    >
      <a-tooltip
        :key="`btn-${index}`"
        :style="{ padding, margin, flex, width: (button.options || {}).width || 'unset' }"
      >
        <template
          v-if="button.help"
          slot="title"
        >
          <!-- eslint-disable vue/no-v-html -->
          <div v-html="tr(button.help, true)"/>
        </template>
        <template v-if="!Array.isArray(button.items) || button.items.length === 0">
          <component
            :is="button.popconfirm ? 'a-popconfirm' : 'div'"
            :ok-text="$t('_.yes')"
            :cancel-text="$t('_.no')"
            :brezel-button-type="button.type"
            :disabled="button.loading"
            @confirm="buttonClick(getCallbackArgs(button, index, false))"
          >
            <template #title>
              {{
                typeof button.popconfirm === 'string' ? tr('popconfirm.' + button.popconfirm, true) : $t('_.are_you_sure')
              }}
            </template>
            <component
              :is="button.link && button.link.external ? 'a' : 'div'"
              v-bind="button.link && button.link.external ? button.link : {}"
            >
              <a-tooltip :title="iconOnly || button.iconOnly ? $t(getButtonName(button)) : null">
                <a-badge :count="button.badge">
                  <a-button
                    :ref="'button-' + index"
                    v-bind="{...button, ...(button.options || {}).attrs}"
                    :block="component.options.display_style === 'block'"
                    :style="{ display: 'block', width: button.icon ? '' : '100%' }"
                    :type="button.style"
                    :size="button.size || size"
                    :html-type="button.htmlType || (button.form ? 'submit' : 'button')"
                    :disabled="isDisabled(button)"
                    :class="(button.options || {}).class"
                    :aria-label="tr(getButtonName(button), true)"
                    @click="!button.popconfirm ? buttonClick(getCallbackArgs(button, index)) : ''"
                  >
                    {{ (!iconOnly || button.forceTitle) && !button.iconOnly ? tr(getButtonName(button), true) : '' }}
                  </a-button>
                </a-badge>
              </a-tooltip>
            </component>
          </component>
        </template>
        <template v-else>
          <a-dropdown
            :ref="'button-dropdown-' + index"
            v-model="visibleDropdowns[index]"
            :trigger="['click']"
            :brezel-button-type="button.type"
            @child-done="hideDropdown(index)"
            :disabled="filterHiddenButtons(button.items).length === 0"
          >
            <template #overlay>
              <a-menu>
                <component
                  :is="dropDownItem.title ? 'a-menu-item' : 'a-menu-divider'"
                  v-for="(dropDownItem, dropDownItemIndex) in filterHiddenButtons(button.items)"
                  :key="dropDownItemIndex"
                  :disabled="dropDownItem.loading"
                  :class="'dropdown-menu-btn-' + dropDownItem.identifier"
                  @click="buttonClick(getCallbackArgs(dropDownItem, index, true))"
                >
                  <component
                    :is="dropDownItem.link && dropDownItem.link.external ? 'a' : 'div'"
                    v-bind="dropDownItem.link && dropDownItem.link.external ? dropDownItem.link : {}"
                  >
                    <a-icon
                      v-if="dropDownItem.icon || dropDownItem.loading"
                      :type="dropDownItem.loading ? 'loading' : dropDownItem.icon"
                    />
                    {{ tr(getButtonName(dropDownItem), true) }}
                  </component>
                </component>
              </a-menu>
            </template>
            <a-button
              :block="component.options.display_style === 'block'"
              :style="{ display: 'block', width: button.icon ? '' : '100%', height: 'auto' }"
              :size="button.size || size"
              :disabled="isDisabled(button)"
              :class="(button.options || {}).class"
              :aria-label="tr(getButtonName(button), true)"
              :type="button.style"
            >
              <a-icon
                v-if="button.icon"
                :type="button.icon"
              />
              {{ (!iconOnly || button.forceTitle) && !button.iconOnly ? tr(getButtonName(button), true) : '' }}
              <a-icon type="down"/>
            </a-button>
          </a-dropdown>
        </template>
      </a-tooltip>
    </template>
  </div>
</template>

<script>
import LayoutComponent from '@/components/layout-components/LayoutComponent'

export default {
  name: 'LayoutComponentButtons',
  extends: LayoutComponent,
  props: {
    'resource': {
      type: Object,
      default: undefined,
    },
    'value': {
      type: [String, Number, Object, Array, Boolean],
      default: undefined,
    },
    'selections': {
      type: [Array, Object, Boolean],
      default: undefined,
    },
    'disabled': {
      type: Boolean,
      default: undefined,
    },
    size: {
      type: String,
      default: 'default',
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      renderComponent: true,
      visibleDropdowns: [],
    }
  },
  computed: {
    buttons () {
      return this.options.buttons.filter(b => !(b.options && b.options.hidden_from_frontend))
    },
    display () {
      if (this.component.options.display_style === 'block') {
        return 'block'
      }
      return 'flex'
    },
    className () {
      if (this.component.options.class) { return this.component.options.class } else { return '' }
    },
    flex () {
      if (this.component.options.display_style === 'scrollable' ||
        this.component.options.display_style === 'block') {
        return null
      }
      return this.component.options.flex || 'none'
    },
    flexWrap () {
      if (this.component.options.display_style === 'scrollable' ||
        this.component.options.display_style === 'block') {
        return null
      }
      return 'wrap'
    },
    justifyContent () {
      if (this.component.options.display_style === 'scrollable' ||
        this.component.options.display_style === 'block') {
        return null
      }
      return this.component.options.justify_content || 'flex-start'
    },
    overflowX () {
      if (this.component.options.display_style === 'scrollable') {
        return 'auto'
      }
      return 'none'
    },
    margin () {
      if (this.component.options.display_style === 'block') {
        return 'none'
      }
      return 0
    },
    padding () {
      if (this.component.options.display_style === 'block') {
        return '4px 8px'
      }
      return '8px 4px'
    },
    containerPadding () {
      if (this.component.options.display_style === 'block') {
        return 'none'
      }
      return '0 4px'
    },
  },
  methods: {
    /**
     * As Ant uses this array to store what dropdown is open, we set it to false and ultimately reset it
     * when we get the signal that a call from inside the dropdown was completed
     * @param index
     */
    hideDropdown (index) {
      this.visibleDropdowns[index] = false
      this.$nextTick()
        .then(() => {
          this.visibleDropdowns[index] = undefined
        })
    },
    getButtonName (button) {
      return button.title ? button.title : (button.identifier ? 'buttons.' + button.identifier : '')
    },
    /**
     * Prepares Arguments for the button callback to use later.
     * If an index is defined, it assumes it has a parent and passes it on so the `child-done` can be fired once the callback completes
     * If isDropdown is true, it uses a different ref prefix to get the button element
     *  @param button
     * @param {Number|Boolean} index
     * @param {Number|Boolean} isDropdown
     * @return CallbackObject
     */
    getCallbackArgs (button, index = false, isDropdown = false) {
      return {
        buttonName: this.getButtonName(button),
        target: button,
        component: this,
        parent: this.getParentForButton(index, isDropdown),
        value: this.value,
        selected: this.selections,
      }
    },
    getParentForButton (buttonIndex, isDropdownButton) {
      if (!buttonIndex) return undefined
      if (isDropdownButton) {
        return this.$refs['button-dropdown-' + buttonIndex][0]
      } else {
        return this.$refs['button-' + buttonIndex][0]
      }
    },
    isDisabled (button) {
      if (button && button.options && button.options.disabled === true) {
        return true
      }
      return this.disabled === true
    },
    filterHiddenButtons (items) {
      return items.filter(b => !(b.options && b.options.hidden_from_frontend))
    },
  },
}
</script>

<style lang="scss">
.button-component {
  align-items: center;

  .ant-btn .anticon.anticon-down {
    display: none;
  }
}

.button-component > div {
  &:first-of-type {
    margin-top: 4px;
  }

  &:last-of-type {
    margin-bottom: 4px;
  }
}
</style>
